import { lazy } from 'react';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import { Navigate } from 'react-router-dom';
import { ROUTES } from './routePaths';
import Seo from '../components/seo/Seo';

// Lazy loaded components
const Home = lazy(() => import('../screens/home/Home'));
const Login = lazy(() => import('../screens/auth/Login'));
const ForgotPassword = lazy(() => import('../screens/auth/ForgotPassword'));
const Register = lazy(() => import('../screens/auth/Register'));
const PricingPlans = lazy(() => import('../screens/subscribe/PricingPlans'));
const TendersPage = lazy(() => import('../screens/tenders/TendersPage'));
const BrowseTenders = lazy(() => import('../screens/tenders/filters/TenderByKeywords'));
const PoliticalRegions = lazy(() => import('../screens/tenders/filters/PoliticalRegions'));
const GeoRegions = lazy(() => import('../screens/tenders/filters/GeoRegions'));
const Dashboard = lazy(() => import('../screens/dashboard/Dashboard'));
const TenderDetails = lazy(() => import('../screens/tenders/TenderDetails'));
const Contracts = lazy(() => import('../screens/contracts/Contracts'));
const FundingAgencies = lazy(() => import('../screens/tenders/filters/fundingagencies'));
const Countries = lazy(() => import('../screens/tenders/filters/Countries'));
const UserProfile = lazy(() => import('../screens/user/UserProfile'));
const LiveTenders = lazy(() => import('../screens/tenders/LiveTenders'));
const ResetPassword = lazy(() => import('../screens/auth/ResetPassword'));
const ChangePassword = lazy(() => import('../screens/auth/ChangePassword'));
const AboutUs = lazy(() => import('../screens/about/AboutUs'));
const ContactUs = lazy(() => import('../screens/about/ContactUs'));
const AdvancedSearch = lazy(() => import('../screens/search/AdvancedSearch'));
const TendersByCategory = lazy(() => import('../screens/tenders/filters/TendersByCategory'));
const TermsAndConditions = lazy(() => import('../screens/policies/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('../screens/policies/PrivacyPolicy'));
const RefundPolicy = lazy(() => import('../screens/policies/RefundPolicy'));
const ContractDetails = lazy(() => import('../screens/contracts/ContractDetails'));
const UpdateQuery = lazy(() => import('../screens/dashboard/UpdateQuery'));
const AnalyticDashboard = lazy(() => import('../screens/dashboard/TenderAnalyticsDashboard'));
const PrivateTenderDetails = lazy(() => import('../screens/tenders/PrivateTenderDetails'));
const TenderDocument = lazy(() => import('../screens/tenders/TenderDocument'));
const PrivateContracts = lazy(() => import('../screens/contracts/PrivateContracts'));
const PrivateContractDetails = lazy(() => import('../screens/contracts/PrivateContractDetails'));
const SiteMap = lazy(() => import('../screens/about/SiteMap'));
const Payment = lazy(() => import('../screens/payment/Payment'));
const PaymentSuccess = lazy(() => import('../screens/payment/PaymentSuccess'));
const PaymentFailure = lazy(() => import('../screens/payment/PaymentFailure'));
const TendersSearchEngine = lazy(() => import('../screens/tenders-search-engine'));
const FAQPage = lazy(() => import('../screens/about/FAQPage'));
const Blog = lazy(() => import('../screens/blog'));
const BlogDetail = lazy(() => import('../screens/blog/BlogDetail'));
const SearchEngineForBids = lazy(() => import('../screens/search-engine-for-bids'));
const ExportToExcel = lazy(() => import('../screens/dashboard/ExportToExcel'));
const EmailTenders = lazy(() => import('../screens/tenders/EmailTenders'));
const TenderDatabases = lazy(() => import('../screens/tender-databases'));
const FavouriteTenders = lazy(() => import('../screens/tenders/FavoriteTenders'));
const ExtensiveProcurementDatabase = lazy(() => import('../screens/extensive-procurement-database'));
const ETenderingPlatform = lazy(() => import('../screens/e-tendering-platform'));
const LatestGlobalTenders = lazy(() => import('../screens/latest-global-tenders'));
const LeadingGlobalTenderingPlatform = lazy(() => import('../screens/leading-global-tendering-platform'));
const BidConsultancyService = lazy(() => import('../screens/bid-consultancy-service'));
const GovermentAndPrivateBids = lazy(() => import('../screens/goverment-and-private-bids'));
const ThankYouPage = lazy(() => import('../screens/thank-you/ThankYouPage'));
const PublishTender = lazy(() => import('../screens/publish-tender/PublishTender'));
const RequestDemo = lazy(() => import('../screens/request-demo/RequestDemo'));
const EuropeGovernmentTenders = lazy(() => import('../Sponsored Result/EuropeGovernmentTenders'));
const RegisterFreeSample = lazy(() => import('../Sponsored Result/RegisterFreeSample'));
const DailyTenderAlerts = lazy(() => import('../Sponsored Result/DailyTenderAlerts'));
const PolandGovernmentTenders = lazy(() => import('../Sponsored Result/PolandGovernmentTenders'));
const CountryGovernmentTenders = lazy(() => import('../Sponsored Result/CountryGovernmentTenders'));
const ItSoftwareGovernmentTenders = lazy(() => import('../Sponsored Result/ItSoftwareGovernmentTenders'));
const SectorLandingPage = lazy(() => import('../Sponsored Result/SectorLandingPage'));

// Route configurations
export const routes = [
    {
        path: ROUTES.HOME,
        element: (
            <>
                <Seo route={ROUTES.HOME} />
                <Home />
            </>)
    },
    {
        path: ROUTES.LOGIN,
        element: <Login />
    },
    {
        path: ROUTES.FORGOT_PASSWORD,
        element: <ForgotPassword />
    },
    {
        path: ROUTES.REGISTER,
        element: <Register />
    },
    {
        path: ROUTES.ABOUT_US,
        element: (
            <>
                <Seo route={ROUTES.ABOUT_US} />
                <AboutUs />
            </>
        )
    },
    {
        path: ROUTES.CONTACT_US,
        element: (
            <>
                <Seo route={ROUTES.CONTACT_US} />
                <ContactUs />
            </>
        )
    },
    // Tender related routes
    {
        path: ROUTES.TENDERS,
        element: (
            <>
                <Seo route={ROUTES.TENDERS} />
                <TendersPage />
            </>
        )
    },
    {
        path: ROUTES.TENDER_DETAIL,
        element: <TenderDetails />
    },
    {
        path: ROUTES.TENDERS_FUNDING_AGENCIES,
        element: (
            <>
                <Seo route={ROUTES.TENDERS_FUNDING_AGENCIES} />
                <FundingAgencies />
            </>
        )
    },
    {
        path: ROUTES.TENDERS_BY_COUNTRY,
        element: (
            <>
                <Seo route={ROUTES.TENDERS_BY_COUNTRY} />
                <Countries />
            </>
        )
    },
    {
        path: ROUTES.SUBSCRIBE,
        element: <PricingPlans />
    },
    {
        path: ROUTES.CONTRACTS,
        element: (
            <>
                <Seo route={ROUTES.CONTRACTS} />
                <Contracts />
            </>
        )
    },
    {
        path: ROUTES.CONTRACT_DETAILS,
        element: <ContractDetails />
    },
    {
        path: ROUTES.ADVANCED_SEARCH,
        element: (
            <>
                <Seo route={ROUTES.ADVANCED_SEARCH} />
                <AdvancedSearch />
            </>
        )
    },
    {
        path: ROUTES.TENDERS_BY_KEYWORDS,
        element: (
            <>
                <Seo route={ROUTES.TENDERS_BY_KEYWORDS} />
                <BrowseTenders />
            </>
        )
    },
    {
        path: ROUTES.TENDERS_BY_POLITICAL_REGION,
        element: (
            <>
                <Seo route={ROUTES.TENDERS_BY_POLITICAL_REGION} />
                <PoliticalRegions />
            </>
        )
    },
    {
        path: ROUTES.TENDERS_BY_GEO_REGION,
        element: (
            <>
                <Seo route={ROUTES.TENDERS_BY_GEO_REGION} />
                <GeoRegions />
            </>
        )
    },
    {
        path: ROUTES.TENDERS_BY_CATEGORY,
        element: (
            <>
                <Seo route={ROUTES.TENDERS_BY_CATEGORY} />
                <TendersByCategory />
            </>
        )
    },
    {
        path: ROUTES.TERMS_AND_CONDITIONS,
        element: (
            <>
                <Seo route={ROUTES.TERMS_AND_CONDITIONS} />
                <TermsAndConditions />
            </>
        )
    },
    {
        path: ROUTES.PRIVACY_POLICY,
        element: (
            <>
                <Seo route={ROUTES.PRIVACY_POLICY} />
                <PrivacyPolicy />
            </>
        )
    },
    {
        path: ROUTES.REFUND_POLICY,
        element: (
            <>
                <Seo route={ROUTES.REFUND_POLICY} />
                <RefundPolicy />
            </>
        )
    },
    {
        path: ROUTES.SITE_MAP,
        element: (
            <>
                <Seo route={ROUTES.SITE_MAP} />
                <SiteMap />
            </>
        )
    },
    {
        path: ROUTES.SUBSCRIBE_PAYMENT,
        element: <Payment />
    },
    {
        path: ROUTES.PAYMENT_SUCCESS,
        element: <PaymentSuccess />
    },
    {
        path: ROUTES.PAYMENT_CANCEL,
        element: <Navigate to={ROUTES.SUBSCRIBE} />
    },
    {
        path: ROUTES.PAYMENT_FAILURE,
        element: <PaymentFailure />
    },
    {
        path: ROUTES.SEARCH_ENGINE_FOR_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.SEARCH_ENGINE_FOR_TENDERS} />
                <TendersSearchEngine />
            </>
        )
    },
    {
        path: ROUTES.FAQ,
        element: (
            <>
                <Seo route={ROUTES.FAQ} />
                <FAQPage />
            </>
        )
    },
    {
        path: ROUTES.BLOG,
        element: (
            <>
                <Seo route={ROUTES.BLOG} />
                <Blog />
            </>
        )
    },
    {
        path: ROUTES.BLOG_DETAIL,
        element: <BlogDetail />
    },
    {
        path: ROUTES.SEARCH_ENGINE_FOR_BIDS,
        element: (
            <>
                <Seo route={ROUTES.SEARCH_ENGINE_FOR_BIDS} />
                <SearchEngineForBids />
            </>
        )
    },
    {
        path: ROUTES.TENDER_DATABASES,
        element: <>
            <Seo route={ROUTES.TENDER_DATABASES} />
            <TenderDatabases />
        </>
    },
    {
        path: ROUTES.TENDERING_PLATFORM,
        element: <>
            <Seo route={ROUTES.TENDERING_PLATFORM} />
            <ETenderingPlatform />
        </>
    },
    {
        path: ROUTES.EXTENSIVE_PROCUREMENT_DATABASE,
        element: <>
            <Seo route={ROUTES.EXTENSIVE_PROCUREMENT_DATABASE} />
            <ExtensiveProcurementDatabase />
        </>
    },
    {
        path: ROUTES.LATEST_GLOBAL_TENDERS,
        element: <>
            <Seo route={ROUTES.LATEST_GLOBAL_TENDERS} />
            <LatestGlobalTenders />
        </>
    },
    {
        path: ROUTES.LEADING_GLOBAL_TENDERING_PLATFORM,
        element: <>
            <Seo route={ROUTES.LEADING_GLOBAL_TENDERING_PLATFORM} />
            <LeadingGlobalTenderingPlatform />
        </>
    },
    {
        path: ROUTES.BID_CONSULTANCY_SERVICE,
        element: <>
            <Seo route={ROUTES.BID_CONSULTANCY_SERVICE} />
            <BidConsultancyService />
        </>
    },
    {
        path: ROUTES.GOVERMENT_AND_PRIVATE_BIDS,
        element: <>
            <Seo route={ROUTES.GOVERMENT_AND_PRIVATE_BIDS} />
            <GovermentAndPrivateBids />
        </>
    },
    {
        path: ROUTES.THANK_YOU,
        element: (
            <>
                <Seo route={ROUTES.THANK_YOU} />
                <ThankYouPage />
            </>
        )
    },
    {
        path: ROUTES.PUBLISH_TENDER,
        element: (
            <>
                <Seo route={ROUTES.PUBLISH_TENDER} />
                <PublishTender />
            </>
        )
    },
    {
        path: ROUTES.REQUEST_DEMO,
        element: <RequestDemo />
    },
    {
        path: ROUTES.EUROPE_GOVERNMENT_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.EUROPE_GOVERNMENT_TENDERS} />
                <EuropeGovernmentTenders />
            </>
        )
    },
    {
        path: ROUTES.REGISTER_FREE_SAMPLE,
        element: (
            <>
                <Seo route={ROUTES.REGISTER_FREE_SAMPLE} />
                <RegisterFreeSample />
            </>
        )
    },
    {
        path: ROUTES.DAILY_TENDER_ALERTS,
        element: (
            <>
                <Seo route={ROUTES.DAILY_TENDER_ALERTS} />
                <DailyTenderAlerts />
            </>
        )
    },
    {
        path: ROUTES.POLAND_GOVERNMENT_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.POLAND_GOVERNMENT_TENDERS} />
                <PolandGovernmentTenders />
            </>
        )
    },
    {
        path: ROUTES.IT_SOFTWARE_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.IT_SOFTWARE_TENDERS} />
                <ItSoftwareGovernmentTenders />
            </>
        )
    },
    {
        path: ROUTES.ERP_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.ERP_TENDERS} />
                <SectorLandingPage sectorId="erp" />
            </>
        )
    },
    {
        path: ROUTES.ENGINEERING_CONSULTANCY_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.ENGINEERING_CONSULTANCY_TENDERS} />
                <SectorLandingPage sectorId="engineering-consultancy" />
            </>
        )
    },
    {
        path: ROUTES.ENVIRONMENTAL_CONSULTANCY_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.ENVIRONMENTAL_CONSULTANCY_TENDERS} />
                <SectorLandingPage sectorId="environmental-consultancy" />
            </>
        )
    },
    {
        path: ROUTES.HEALTHCARE_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.HEALTHCARE_TENDERS} />
                <SectorLandingPage sectorId="healthcare" />
            </>
        )
    },
    {
        path: ROUTES.CYBERSECURITY_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.CYBERSECURITY_TENDERS} />
                <SectorLandingPage sectorId="cybersecurity" />
            </>
        )
    },
    {
        path: ROUTES.GIS_GEOSPATIAL_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.GIS_GEOSPATIAL_TENDERS} />
                <SectorLandingPage sectorId="gis-geospatial" />
            </>
        )
    },
    {
        path: ROUTES.CONSTRUCTION_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.CONSTRUCTION_TENDERS} />
                <SectorLandingPage sectorId="construction" />
            </>
        )
    },
    {
        path: ROUTES.RENEWABLE_ENERGY_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.RENEWABLE_ENERGY_TENDERS} />
                <SectorLandingPage sectorId="renewable-energy" />
            </>
        )
    },
    {
        path: ROUTES.SOLAR_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.SOLAR_TENDERS} />
                <SectorLandingPage sectorId="solar" />
            </>
        )
    },
    {
        path: ROUTES.OIL_GAS_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.OIL_GAS_TENDERS} />
                <SectorLandingPage sectorId="oil-gas" />
            </>
        )
    },
    {
        path: ROUTES.RAIL_METRO_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.RAIL_METRO_TENDERS} />
                <SectorLandingPage sectorId="rail-metro" />
            </>
        )
    },
    {
        path: ROUTES.WATER_WASTEWATER_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.WATER_WASTEWATER_TENDERS} />
                <SectorLandingPage sectorId="water-wastewater" />
            </>
        )
    },
    {
        path: ROUTES.DEFENCE_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.DEFENCE_TENDERS} />
                <SectorLandingPage sectorId="defence" />
            </>
        )
    },
    {
        path: ROUTES.DRONE_UAV_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.DRONE_UAV_TENDERS} />
                <SectorLandingPage sectorId="drone-uav" />
            </>
        )
    },
    {
        path: ROUTES.SMART_CITY_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.SMART_CITY_TENDERS} />
                <SectorLandingPage sectorId="smart-city" />
            </>
        )
    },
    {
        path: ROUTES.TELECOM_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.TELECOM_TENDERS} />
                <SectorLandingPage sectorId="telecom" />
            </>
        )
    },
    {
        path: ROUTES.AI_DIGITAL_TRANSFORMATION_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.AI_DIGITAL_TRANSFORMATION_TENDERS} />
                <SectorLandingPage sectorId="ai-digital-transformation" />
            </>
        )
    },
    {
        path: ROUTES.CONTRACT_AWARDS_LANDING,
        element: (
            <>
                <Seo route={ROUTES.CONTRACT_AWARDS_LANDING} />
                <SectorLandingPage sectorId="contract-awards" />
            </>
        )
    },
    {
        path: ROUTES.WORLD_BANK_UN_TENDERS,
        element: (
            <>
                <Seo route={ROUTES.WORLD_BANK_UN_TENDERS} />
                <SectorLandingPage sectorId="world-bank-un" />
            </>
        )
    },
    {
        path: '/:politicalSlug-political-region-government-tenders-and-procurement-opportunities',
        element: (
            <>
                <CountryGovernmentTenders />
            </>
        )
    },
    {
        path: '/:regionSlug-region-government-tenders-and-procurement-opportunities',
        element: (
            <>
                <CountryGovernmentTenders />
            </>
        )
    },
    {
        path: '/:countrySlug-government-tenders-and-procurement-opportunities',
        element: (
            <>
                <CountryGovernmentTenders />
            </>
        )
    },
    // Protected Routes
    {
        path: ROUTES.DASHBOARD,
        element: <ProtectedRoute><Dashboard /></ProtectedRoute>
    },
    {
        path: ROUTES.FAVOURITE_TENDERS,
        element: <ProtectedRoute><FavouriteTenders /></ProtectedRoute>
    },
    {
        path: ROUTES.CONTRACTS_PRIVATE,
        element: <ProtectedRoute><PrivateContracts /></ProtectedRoute>
    },
    {
        path: ROUTES.CONTRACTS_PRIVATE_DETAIL,
        element: <ProtectedRoute><PrivateContractDetails /></ProtectedRoute>
    },
    {
        path: ROUTES.TENDERS_LIVE,
        element: <ProtectedRoute><LiveTenders /></ProtectedRoute>
    },
    {
        path: ROUTES.ADVANCED_SEARCH_LIVE,
        element: <ProtectedRoute><AdvancedSearch /></ProtectedRoute>
    },
    {
        path: ROUTES.PROFILE,
        element: <ProtectedRoute><UserProfile /></ProtectedRoute>
    },
    {
        path: ROUTES.RESET_PASSWORD,
        element: <ProtectedRoute><ResetPassword /></ProtectedRoute>
    },
    {
        path: ROUTES.CHANGE_PASSWORD,
        element: <ProtectedRoute><ChangePassword /></ProtectedRoute>
    },
    {
        path: ROUTES.TENDERS_PRIVATE,
        element: <ProtectedRoute><PrivateTenderDetails /></ProtectedRoute>
    },
    {
        path: ROUTES.TENDERS_PRIVATE_DETAIL,
        element: <ProtectedRoute><PrivateTenderDetails /></ProtectedRoute>
    },
    {
        path: ROUTES.TENDER_DOCUMENT,
        element: <ProtectedRoute><TenderDocument /></ProtectedRoute>
    },
    {
        path: ROUTES.UPDATE_QUERY,
        element: <ProtectedRoute><UpdateQuery /></ProtectedRoute>
    },
    {
        path: ROUTES.EXPORT_TO_EXCEL,
        element: <ProtectedRoute><ExportToExcel /></ProtectedRoute>
    },
    {
        path: ROUTES.ANALYTIC_DASHBOARD,
        element: <ProtectedRoute><AnalyticDashboard /></ProtectedRoute>
    },
    {
        path: ROUTES.EMAIL_TENDERS,
        element: <ProtectedRoute><EmailTenders /></ProtectedRoute>
    },
];